/* Answer-rendering styles shared across the results panel's per-question-type components — mirrors
the take-assessment side's question-types/QuestionAnswer.module.css convention. Page chrome (nav bar,
detail header, grade section) lives in ../QuizResultPanel.module.css instead. */

/* ── MC / TF ─────────────────────────────────────────────────────── */
.optionList {
	display: flex;
	flex-direction: column;
	gap: 6px;
}

.option {
	display: flex;
	align-items: center;
	gap: 8px;
	border-radius: 4px;
	border: 1px solid #e6e6e6;
	background-color: #fff;
	padding: 10px 20px;

	&.correct {
		background: #dbe7e1;
		border: 1px solid #a1d2ba33;
	}

	&.incorrect {
		background: #f8dcdb;
		border: 1px solid #e3a7a533;
	}
}

.optionIndexBox {
	width: 20px;
	height: 20px;
	display: flex;
	align-items: center;
	justify-content: center;
	background-color: #e6e6e6;
	border-radius: 4px;
	display: -ms-flexbox;
	flex-shrink: 0;
	color: #344054;
	font-size: 14px;
	line-height: 1;

	&.correct {
		background-color: #a1d2ba;
	}

	&.incorrect {
		background-color: #e3a7a5;
	}

	& svg path {
		stroke-width: 1.5px;
	}
}

/* ── Matching ─────────────────────────────────────────────────────── */
.matchingPairGridyLayout {
	display: grid;
	gap: 10px;
	align-items: center;
	grid-template-columns: repeat(2, minmax(0, 1fr));
}

/* ── Correct answer section ──────────────────────────────────────────────── */

.correctAnswerSection {
	display: flex;
	flex-direction: column;
	gap: 4px;
	margin-top: 15px;
}

.correctAnswerList {
	display: flex;
	flex-direction: column;
	gap: 2px;
}

.correctAnswerItem:global(.MuiTypography-root) {
	font-size: 0.875rem;
	color: #374151;
}

.correctAnswerLabel {
	font-weight: 600;
}

/* ── Inline blank rendering (FillInTheBlank; also reused by Dropdown / DragAndDrop) ── */
.blankCard {
	display: flex;
	flex-direction: column;
	gap: 12px;
	background: #fafafa;
	border-radius: 10px;
	border: 1px solid #e5e5e5;
	padding: 16px 20px;

	& .fitbText {
		margin-bottom: 0;
	}
}

.blankInlineWrapper {
	font-size: 0.875rem;
	line-height: 2.6;
	word-break: break-word;
}

.blankInlinePlaceholder {
	display: inline-block;
	text-align: center;
	letter-spacing: -0.5px;
	color: #374151;
}

.blankInlineChip {
	display: inline-flex;
	align-items: center;
	gap: 5px;
	border-radius: 4px;
	overflow: hidden;
	font-size: 14px;
	line-height: 1.5;
	width: fit-content;
	padding: 5px;
	margin: 2px;
	min-height: 32px;

	/* Unanswered sits on the card's #FAFAFA background, so its border needs to be visibly darker
	than the #e6e6e6 used elsewhere — that value is nearly indistinguishable from the card itself. */
	border: 1px solid #c4c4c4;
	background-color: #fff;

	&.correct {
		background: #dbe7e1;
		border: 1px solid #a1d2ba33;
	}

	&.incorrect {
		background: #f8dcdb;
		border: 1px solid #e3a7a533;
	}
}

.blankInlineChipIcon {
	flex-shrink: 0;
	width: 20px;
	height: 20px;
	display: flex;
	align-items: center;
	justify-content: center;
	gap: 5px;
	background-color: #e6e6e6;
	border-radius: 4px;
	display: -ms-flexbox;
	color: #344054;
	font-size: 14px;
	line-height: 1;

	&.correct {
		background-color: #a1d2ba !important;
	}

	&.incorrect {
		background-color: #e3a7a5 !important;
	}

	& svg {
		width: 16px;
		height: auto;
	}

	& svg path {
		stroke-width: 1.5px;
	}
}

.blankInlineChipText {
	padding: 0 8px;
	align-self: center;
	line-height: 1;
}

/* ── Coding ─────────────────────────────────────────────────────────────── */

.codingAnswerWrapper {
	display: flex;
	flex-direction: column;
	gap: 6px;
}

.codingAnswerLanguage {
	align-self: flex-start;
	font-size: 0.75rem;
	font-weight: 600;
	color: #6b7280;
	background: #f3f4f6;
	border-radius: 4px;
	padding: 2px 8px;
}

/* Wraps a read-only CodeMirror instance — mirrors the take-assessment side's .codeEditor container
styling, but with no fixed height, since a review display should show all the code rather than
scroll it in a small fixed viewport. */
.codingAnswerCode {
	width: 100%;
	background: #fafafa;
	border: 1px solid #e5e5e5;
	border-radius: 10px;
	overflow: hidden;

	& :global(.cm-editor) {
		font-family: "SFMono-Regular", Consolas, "Liberation Mono", Menlo, monospace;
		font-size: 13px;
	}

	& :global(.cm-gutters) {
		background: transparent;
		border-right: none;
		color: #b0b0b0;
	}

	& :global(.cm-content) {
		padding: 12px 0;
	}

	& :global(.cm-line) {
		padding: 0 16px;
	}
}

/* ── Spreadsheet ─────────────────────────────────────────────────────────── */

.spreadsheetTableWrapper {
	border: 1px solid #e6e6e6;
	border-radius: 4px;
	overflow-x: auto;
	width: fit-content;
	max-width: 100%;
}

.spreadsheetTable {
	border-collapse: collapse;
	table-layout: fixed;
	width: max-content;

	& thead tr:first-child th {
		border-top: none;
	}

	& tr:last-child > * {
		border-bottom: none;
	}

	& tr > *:first-child {
		border-left: none;
	}

	& tr > *:last-child {
		border-right: none;
	}
}

.spreadsheetCornerCell,
.spreadsheetColHeader,
.spreadsheetRowHeader {
	background: #f9fafb;
	border: 1px solid #e6e6e6;
	box-sizing: border-box;
	color: #262626;
	font-size: 13px;
	font-weight: 600;
	width: 40px;
	padding: 6px 8px;
	text-align: center;
}

.spreadsheetColHeader {
	text-align: left;
}

.spreadsheetCornerCell,
.spreadsheetRowHeader {
	left: 0;
	position: sticky;
	z-index: 1;
}

.spreadsheetCornerCell {
	z-index: 2;
}

.spreadsheetCell {
	border: 1px solid #e6e6e6;
	box-sizing: border-box;
	background: #fff;
	font-size: 14px;
	height: 40px;
	padding: 0 10px;
	vertical-align: middle;
	text-align: left;
}

.spreadsheetCellText {
	color: #262626;
	overflow: hidden;
	text-overflow: ellipsis;
	white-space: nowrap;
	/* Flex items default to min-width: auto, which lets content overflow instead of shrinking to
	trigger the ellipsis above — this is what actually makes truncation take effect in the row. */
	min-width: 0;
}

.spreadsheetCellContent {
	display: flex;
	align-items: center;
	gap: 5px;
	width: 150px;
	height: 100%;
}

.spreadsheetAnswerCell {
	&.correct {
		background: #dbe7e1;
	}

	&.incorrect {
		background: #f8dcdb;
	}
}

/* ── Audio Recording ───────────────────────────────────────────────────── */

/* Matches the take-assessment side's .audioRecordingReviewBox — same cap so a video/audio recording
answer doesn't stretch to the full width of the grading panel. */
.audioAnswerWrapper {
	max-width: 640px;
}

/* ── File Submission ───────────────────────────────────────────────────── */

.fileAnswerList {
	display: flex;
	flex-direction: column;
	gap: 8px;
}

.fileAnswerItem {
	width: 100%;
}
